/**
 * Single item in a data table.
 * @class DataTableItemElement
 */
class DataTableItemElement extends DataTableCommon {

  /**
   * Template for DataTableItemElement.
   * @param {object} args Template arguments.
   * @param {string[]} args.columns List of column ids.
   * @param {string[]} args.sticky List of column ids that should be sticky.
   * @param {string|number} args.numIndex Index of the item in the data table.
   * @param {boolean} args.isExpanded Whether the row is expanded.
   * @returns {string} DOM that should be rendered into the shadow root.
   * @protected
   */
  static template(args = {}) {

    // default values for template arguments
    args = {
      columns: [],
      sticky: [],
      numIndex: '',
      isExpanded: false,
      ...args
    };

    return /*html*/ `
      <template>
        <style>
          ${super.styles}

          :host {
            display: contents;
          }

          :host(:hover) .inner {
            background-color: var(--color-contrast-1);
          }

          .table__row--expandable {
            display: none;
          }

          .table__row--expandable .table__cell {
            padding-bottom: calc(var(--cell-padding-x) * 2);
            border-top: none;
          }

          :host([expanded]) .table__row--expandable {
            display: table-row;
          }


        </style>
        <tr class="table__row" part="row" role="row" aria-rowindex="${args.numIndex}" aria-expanded="${args.isExpanded}" aria-controls="expandable-content">
          ${args.columns.map(id => {
            return /* html */`
              <td
                role="cell"
                part="col-${id} col"
                data-col="${id}"
                class="table__cell table__cell--head ${args.sticky?.includes(id) ? 'table__cell--sticky' : ''}"
                ${args.sticky?.includes(id) ? `
                  style="--sticky-cell-offset-left: var(--sticky-cell-offset-left-${id},0); --sticky-cell-offset-right: var(--sticky-cell-offset-right-${id},0)"
                  data-sticky-active="false"
                ` : ''}
              >
                <div class="inner">
                  <slot name="${id}"></slot>
                </div>
              </td>
              `
          }).join('') ?? ''}
        </tr>
        <tr class="table__row table__row--expandable" id="expandable-content">
          <td class="table__cell" colspan="${args.columns.length}" part="expandable">
            <slot>no content</slot>
          </td>
        </tr>
      </template>
    `;
  }

  constructor() {
    super();    
  }

  render() {
    if (!this.dataTableElement) {
      return;
    }

    const columns = this.dataTableElement.columns;
    const sticky = this.dataTableElement.sticky;

    const numIndex = this._calcRowIndex();

    this.shadowRoot.innerHTML = this.constructor.template({
      columns: columns,
      sticky: sticky,
      numIndex: numIndex,
      isExpanded: this.expanded
    });

    const template = this.shadowRoot.querySelector('template');
    const clone = template.content.cloneNode(true);
    this.shadowRoot.appendChild(clone);

    this.dataTableElement.addEventListener('main-slot-change', this._onSlotChange.bind(this));
  }

  static get observedAttributes() {
    return [
      'expanded'
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }
    this[name] = newValue;
  }

  get expanded() {
    const value = this.getAttribute('expanded');

    return value === '' || !!value;
  }

  set expanded(value) {
    const castedValue = value === '' || !!value;
    if (castedValue) {
      this._setAttributeIfNotAlreadySet('expanded', 'true');
    } else {
      this.removeAttribute('expanded');
    }
    
    // Update aria-expanded attribute
    const mainRow = this.shadowRoot?.querySelector('[role="row"]');
    if (mainRow) {
      mainRow.setAttribute('aria-expanded', castedValue ? 'true' : 'false');
    }
  }
  
  _calcRowIndex() {
    if (!this.dataTableElement) {
      return 1;
    }

    const numIndex = Array.from(this.dataTableElement.children)
      .filter(child => child.tagName === 'DATA-TABLE-ITEM')
      .indexOf(this) + 1;

    return numIndex;
  }

  _onSlotChange () {
    if (!this.dataTableElement) {
      return;
    }

    this.shadowRoot.querySelector('[role="row"]')
      .setAttribute('aria-rowindex', this._calcRowIndex());
  }
}


window.DataTableItemElement = DataTableItemElement;
customElements.define('data-table-item', DataTableItemElement);